/**
 * ADM-031 and ADM-034. Rule changes save as a new candidate version. Live drafting is untouched
 * until the version is promoted. Rollback restores an earlier live version. Both are logged.
 */
import * as stylex from "@stylexjs/stylex";
import type { AdminEngineVersion, AdminEngineVersions } from "@lattice/core";
import { ROUTES } from "@lattice/core";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { useState } from "react";
import { api } from "./api.ts";
import type { Column } from "./layout.tsx";
import { FilterChip, FilterRow, History, PageHead, Panel, Section, Split, Table } from "./layout.tsx";
import { href } from "./router.ts";
import { useLoad } from "./load.ts";
import { ReasonForm } from "./ReasonForm.tsx";
import { Button, ErrorText, Field, Status } from "./ui.tsx";
import { moment, refusal } from "./words.ts";
function statusOf(row: AdminEngineVersion) {
  if (row.status === "live") return <Status tone="ok">Live</Status>;
  if (row.status === "candidate") return <Status tone="warn">Candidate</Status>;
  return <Status tone="neutral">Retired</Status>;
}
function VersionActions({
  row,
  onSaved
}: {
  readonly row: AdminEngineVersion;
  readonly onSaved: (next: AdminEngineVersions) => void;
}) {
  const [open, setOpen] = useState<"promote" | "rollback" | null>(null);
  async function submit(reason: string): Promise<string | null> {
    const route = open === "rollback" ? ROUTES.adminRollback : ROUTES.adminPromote;
    const result = await api<AdminEngineVersions>(route, {
      params: {
        versionId: row.id
      },
      body: {
        reason
      }
    });
    if (!result.ok) return refusal(result.error);
    onSaved(result.data);
    return null;
  }
  if (row.status === "live") return null;
  if (open !== null) return <ReasonForm id={`${row.id}-${open}`} hint={open === "promote" ? "Promotion runs the evaluation set and refuses new safety failures." : "This restores an earlier live version."} action={open === "promote" ? "Promote to live" : "Roll back to this version"} tone={open === "rollback" ? "danger" : "primary"} onSubmit={submit} onCancel={() => setOpen(null)} />;
  return <div className="x78zum5 xdt5ytf x1r0x45p">
      {row.status === "candidate" ? <Button onClick={() => setOpen("promote")}>Promote</Button> : <Button tone="quiet" onClick={() => setOpen("rollback")}>
          Roll back
        </Button>}
    </div>;
}
function columns(onSaved: (next: AdminEngineVersions) => void): readonly Column<AdminEngineVersion>[] {
  return [{
    label: "Version",
    cell: row => row.version
  }, {
    label: "Rules",
    phoneLabel: true,
    cell: row => row.ruleSetVersion
  }, {
    label: "Status",
    nowrap: true,
    cell: statusOf
  }, {
    label: "Saved",
    numeric: true,
    phoneLabel: true,
    cell: row => moment(row.createdAt)
  }, {
    label: "",
    wide: true,
    cell: row => <VersionActions row={row} onSaved={onSaved} />
  }];
}
function Editor({
  page,
  onSaved
}: {
  readonly page: AdminEngineVersions;
  readonly onSaved: (next: AdminEngineVersions) => void;
}) {
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [rulesJson, setRulesJson] = useState(page.live?.rulesJson ?? page.starterRulesJson);
  async function save(reason: string): Promise<string | null> {
    const result = await api<AdminEngineVersions>(ROUTES.adminCreateRuleSet, {
      body: {
        version,
        rulesJson,
        notes: notes.trim() === "" ? null : notes,
        reason
      }
    });
    if (!result.ok) return refusal(result.error);
    onSaved(result.data);
    setVersion("");
    setNotes("");
    return null;
  }
  return <Panel padded>
      <ReasonForm id="rules-reason" hint="What the new rule set is for. It stays a candidate until promoted." action="Save as a new version" onSubmit={save} ready={version.trim() !== "" && rulesJson.trim() !== ""}>
        <Field id="rules-version" label="Rule set version" hint="Letters, numbers, dots, underscores and hyphens. Live drafting is unchanged until you promote." value={version} onChange={setVersion} />
        <Field id="rules-notes" label="Notes" value={notes} onChange={setNotes} multiline />
        <Field id="rules-json" label="Rules" hint="JSON in the engine's rule set shape. Invalid JSON is refused." value={rulesJson} onChange={setRulesJson} multiline />
      </ReasonForm>
    </Panel>;
}
function Loaded({
  page,
  view,
  onSaved
}: {
  readonly page: AdminEngineVersions;
  readonly view: "versions" | "history";
  readonly onSaved: (next: AdminEngineVersions) => void;
}) {
  const live = page.live?.version.version ?? "None live";
  return <>
      <PageHead title="Rules and versions" meta={`Live: ${live}`} />
      <FilterRow>
        <FilterChip to={href.rules("versions")} label="Versions" count={page.versions.length} current={view === "versions"} />
        <FilterChip to={href.rules("history")} label="History" count={page.history.length} current={view === "history"} />
      </FilterRow>
      <Split main={view === "history" ? <History rows={page.history} empty="No promotions or saves yet." /> : <Table label="Engine versions" columns={columns(onSaved)} rows={page.versions} rowKey={row => row.id} empty="No versions yet. Save a rule set to create a candidate." />} side={<Section title="New rule set">
            <Editor page={page} onSaved={onSaved} />
          </Section>} />
    </>;
}
export function Rules({
  view
}: {
  readonly view: "versions" | "history";
}) {
  const [found, setFound] = useLoad("engine-versions", () => api<AdminEngineVersions>(ROUTES.adminEngineVersions));
  if (found.state === "loading") return <PageHead title="Rules and versions" meta="Loading" />;
  if (found.state === "failed") return <ErrorText>{refusal(found.error)}</ErrorText>;
  return <Loaded page={found.data} view={view} onSaved={setFound} />;
}